import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Phone, MessageCircle, Send, Loader2 } from 'lucide-react';
import { contactInfo } from '../utils/mockData';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Enquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Message sent successfully!', {
          duration: 4000,
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: 'General Enquiry',
          message: ''
        });
      } else {
        toast.error(data.detail || 'Failed to send message. Please try WhatsApp instead.', {
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try WhatsApp instead.', {
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="section-title">Get In Touch</h2>
          <div className="section-title-underline"></div>
          <p className="section-description">
            Ready to start your fitness journey? Contact me today for a free consultation
          </p>
        </div>

        <div className="contact-container">
          {/* Contact Info */}
          <div className="contact-info">
            <h3 className="contact-info-title">Contact Information</h3>
            <p className="contact-info-description">
              Feel free to reach out through any of these channels. I'm here to help you achieve your fitness goals.
            </p>
            
            <div className="contact-methods">
              <a href="tel:+447787166065" className="contact-method" data-testid="contact-phone">
                <div className="contact-method-icon">
                  <Phone size={24} />
                </div>
                <div>
                  <p className="contact-method-label">Phone</p>
                  <p className="contact-method-value">{contactInfo.phone}</p>
                </div>
              </a>
              
              <a href="mailto:Aaronchoi@acwellnessanddevelopment.com" className="contact-method" data-testid="contact-email">
                <div className="contact-method-icon">
                  <Mail size={24} />
                </div>
                <div>
                  <p className="contact-method-label">Email</p>
                  <p className="contact-method-value">Aaronchoi@acwellnessanddevelopment.com</p>
                </div>
              </a>
              
              <a 
                href="https://wa.me/447787166065?text=Hi%20Aaron%2C%20I%27d%20like%20to%20enquire%20about%20your%20services" 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-method"
                data-testid="contact-whatsapp"
              >
                <div className="contact-method-icon whatsapp">
                  <MessageCircle size={24} />
                </div>
                <div>
                  <p className="contact-method-label">WhatsApp</p>
                  <p className="contact-method-value">Message me directly</p>
                </div>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Your phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="contact-phone-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="subject">Subject *</Label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="form-select"
                  required
                  data-testid="contact-subject-select"
                >
                  <option value="General Enquiry">General Enquiry</option>
                  <option value="Personal Training">Personal Training</option>
                  <option value="Sports Massage">Sports Massage</option>
                  <option value="Seminars">Seminars</option>
                  <option value="Wellness Retreat">Wellness Retreat</option>
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                name="message"
                placeholder="Tell me about your fitness goals..."
                value={formData.message}
                onChange={handleChange}
                rows={5}
                required
                data-testid="contact-message-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="contact-submit-btn"
              size="lg"
              disabled={isSubmitting}
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
